import PropTypes from 'prop-types';
import _get from 'lodash/get';
import { connect } from 'react-redux';
import { useState, useEffect } from 'react';

import ButtonViewGlossary from '../../../atoms/button-view-glossary';
import Icon from '../../../atoms/icon';
import InteractiveElement from '../../../atoms/interactive-element';
import TableColumnHeader from '../../../atoms/table-column-header';
import format from '../../../../utils/format';
import styles from './result-table-header.module.scss';
import useScreenerCommonStore from '../../../../stores/screener/use-screener-common-store';
import useSideRailContext from '../../../../stores/profile/use-side-rail-context';
import { FOOTNOTE_DISCLAIMERS, CHANNELS } from '../../../../shared/screener-constants';
import { augmentClassName } from '../../../../utils/GlobalCssProxy';
import { getTextColor } from '../../../../utils/sharedHelpers';

const className = augmentClassName(styles);

export const ResultTableHeader = ({
  hasClassA,
  performanceQuarterly,
  onChangePerformanceTerm,
  onClickExpandAll,
  expandAll,
  mStarAsOf,
  performanceAsOf,
  setShowSalesCharge,
  showSalesCharge,
  sortingState,
  sortByHeader,
  numberedDisclaimers
}) => {
  const { commonState } = useScreenerCommonStore();
  const componentOffsets = {};
  const { channel } = commonState;
  const pageOffsetsTop = _get(commonState, 'pageOffsetsTop', {});
  if (pageOffsetsTop) componentOffsets.top = pageOffsetsTop;
  const sideRail = useSideRailContext();
  const heading = ' Avg. Annualized Total Returns (%)';
  const getSortingDirection = () => {
    return sortingState.sortDirection === 'desc' ? 'arrow-down' : 'arrow-up';
  };
  const [printing, setPrinting] = useState(false);
  const fontStyle = showSalesCharge ? 'font-italic' : '';
  const isMultipleAverageAnnualReturnsSuperScripts =
    numberedDisclaimers[FOOTNOTE_DISCLAIMERS.AVGANNUALNONTIAATRADITIONAL]?.number &&
    numberedDisclaimers[FOOTNOTE_DISCLAIMERS.AVGANNUALTIAATRADITIONAL]?.number;

  const openGlossary = e => {
    e.stopPropagation();
    sideRail.glossary.show();
  };
  const handleCheckboxChange = () => {
    setShowSalesCharge(!showSalesCharge);
  };

  const switchToQuarterlyBeforePrint = () => {
    onChangePerformanceTerm(true);
  };

  useEffect(() => {
    window.addEventListener('beforeprint', switchToQuarterlyBeforePrint);
    return () => {
      window.removeEventListener('beforeprint', switchToQuarterlyBeforePrint);
      if (printing) {
        setPrinting(false);
      }
    };
  }, []);

  const root = document.documentElement;
  useEffect(() => {
    //If 'Class A' exists in result, set the height of the header to contain checkbox.
    if (hasClassA) {
      //setting a CSS custom property to adjust the height.
      root.style.setProperty('--result-header-height', `${58.5}px`);
    }
  }, [hasClassA]);

  useEffect(() => {
    if (printing) {
      window.print();
      setPrinting(false);
    }
  }, [printing]);
  return (
    <div
      {...className('result-table-header')}
      style={
        channel === CHANNELS.MYTIAA ? {} : { '--header-offset': `${componentOffsets.top || 0}px` }
      }
    >
      <div {...className('result-table-header-background')}></div>
      <div {...className('text-nowrap', 'total-returns-column-group', 'no-print')}>
        <InteractiveElement
          as="span"
          {...className(performanceQuarterly ? 'period-unselected' : 'font-weight-bold')}
          onClick={() => onChangePerformanceTerm(false)}
        >
          Monthly
        </InteractiveElement>
        <span> | </span>
        <InteractiveElement
          as="span"
          {...className(performanceQuarterly ? 'font-weight-bold' : 'period-unselected')}
          onClick={() => onChangePerformanceTerm(true)}
        >
          Quarterly
        </InteractiveElement>
        <span {...className('ml-2', 'text-nowrap')}>
          {heading}{' '}
          <sup>
            {numberedDisclaimers[FOOTNOTE_DISCLAIMERS.AVGANNUALNONTIAATRADITIONAL]?.number}
            {isMultipleAverageAnnualReturnsSuperScripts && ','}
            {numberedDisclaimers[FOOTNOTE_DISCLAIMERS.AVGANNUALTIAATRADITIONAL]?.number}
          </sup>
        </span>
        <ButtonViewGlossary
          className="no-print"
          glossarySection="Performance Returns"
          isScreener
          solid
        />
        <div {...className('date')}>
          As of &nbsp;
          {format.oaDate(performanceAsOf)}
          &nbsp; unless noted
        </div>
        {hasClassA && (
          <div>
            <label
              {...className('d-flex', 'justify-content-center', 'font-weight-bold', fontStyle)}
            >
              <input
                type="checkbox"
                label={''}
                aria-label={'check box to show performance with sales charge included'}
                id={'show-sales-charge'}
                onChange={handleCheckboxChange}
                {...className('checkbox')}
                checked={showSalesCharge}
              />
              &nbsp; Show performance with sales charge included
            </label>
          </div>
        )}

        <div {...className('line')}></div>
      </div>
      <div {...className('total-returns-column-group-print-disclosure')}>
        <div {...className('quarterly-header-text', 'print-only', 'font-weight-bold')}>
          <span>
            Investment Performance As of {mStarAsOf ? format.oaDate(performanceAsOf) : '--'}
          </span>
        </div>
        <p
          {...className(
            'font-family-roboto',
            'inline-disclosure-text',
            'font-weight-normal',
            'mt-2',
            'print-font-size',
            'd-none',
            'd-print-block',
            'print-disclosure-margin',
            'font-weight-bold'
          )}
        >
          {`The performance data quoted represents past performance, and is no guarantee of future results. Your returns and the principal value of your investment will fluctuate so that your mutual fund shares and annuity account accumulation units, and shares or units of the investment vehicles in your model-based accounts, when redeemed, may be worth more or less than their original cost. Current performance may be lower or higher than the performance quoted. The net asset values used to calculate the hypothetical account performance for model-based accounts are compiled using values for underlying funds as of the prior business day and current business day for fixed annuities. For performance current to the most recent month-end, call 800-842-2252 or visit TIAA.org/planinvestmentoptions and enter your plan ID. Performance may reflect waivers or reimbursements of certain expenses. Absent these waivers or reimbursement arrangements, performance results would have been lower. Since Inception performance shown is cumulative for periods less than one year.`}
        </p>
      </div>
      <div {...className('column-glossary')}>
        <button
          data-analytics-linkname="print"
          onClick={() => {
            switchToQuarterlyBeforePrint();
            setTimeout(() => {
              setPrinting(true);
            }, 500);
          }}
          aria-label="print"
          {...className('no-print')}
        >
          <Icon variant="PrinterBlue" />
        </button>
        <ButtonViewGlossary className="no-print" isScreener>
          Glossary
        </ButtonViewGlossary>
      </div>
      <div {...className('column-all-row-toggle')}>
        <button
          {...className('btn-expand', expandAll && 'justify-content-between')}
          onClick={onClickExpandAll}
          aria-expanded={expandAll}
          aria-label={`${expandAll ? 'Close' : 'Expand'} extra content for all rows`}
          tabIndex={'0'}
          // TIAA Analytics Tracking
          data-analytics-linkname={`InvestmentResults:Header:${expandAll ? 'Collapse' : 'Expand'}`}
          data-analytics-section="component"
          data-analytics-componenttype="Toggle"
          // END TIAA Analytics Tracking
        >
          {expandAll ? (
            <>
              <div {...className('expand-down')} />
              <div {...className('expand-up')} />
            </>
          ) : (
            <>
              <div {...className('expand-up', 'mt-2')} />
              <div {...className('expand-down', 'mb-2')} />
            </>
          )}
        </button>
      </div>
      <div {...className('column-name')}>
        <button
          aria-label="Invest name button, click this button to sort result table by invest name."
          onClick={() => sortByHeader('investmentName')}
          // TIAA Analytics Tracking
          data-analytics-linkname="Name_[symbol]:InvestmentResults:Header"
          data-analytics-section="component"
          data-analytics-componenttype="Sort"
          // END TIAA Analytics Tracking
        >
          <TableColumnHeader
            sortingState={sortingState}
            sortString={'investmentName'}
            sortLabel={'Name [Symbol]'}
          />
          {sortingState.sortBy === 'investmentName' ? (
            <div {...className(getSortingDirection())} />
          ) : (
            <div {...className('arrow-unselected')} />
          )}
        </button>
        <div>
          <button
            {...className(
              'text-nowrap',
              'screener-result-table-results-sort',
              getTextColor('productType', sortingState)
            )}
            aria-label="Product type button, click this button to sort result table by product type."
            onClick={() => sortByHeader('productType')}
            // TIAA Analytics Tracking
            data-analytics-linkname="Product_type:InvestmentResults:Header"
            data-analytics-section="component"
            data-analytics-componenttype="Sort"
            // END TIAA Analytics Tracking
          >
            Product Type
            {sortingState.sortBy === 'productType' ? (
              <div {...className(getSortingDirection())} />
            ) : (
              <div {...className('arrow-unselected')} />
            )}
          </button>
          <span>/ Share Class</span>
        </div>
      </div>
      <div {...className('column-asset-class')}>
        <button
          {...className('flex-nowrap', 'screener-result-table-results-sort')}
          aria-label="Asset class button, click this button to sort result table by asset class."
          onClick={() => sortByHeader('assetClass')}
          // TIAA Analytics Tracking
          data-analytics-linkname="Asset_class:InvestmentResults:Header"
          data-analytics-section="component"
          data-analytics-componenttype="Sort"
          // END TIAA Analytics Tracking
        >
          <TableColumnHeader
            sortingState={sortingState}
            sortString={'assetClass'}
            sortLabel={'Asset Class'}
          />
          {sortingState.sortBy === 'assetClass' ? (
            <div {...className(getSortingDirection())} />
          ) : (
            <div {...className('arrow-unselected')} />
          )}
        </button>
      </div>
      <div {...className('column-ytd')}>
        <button
          {...className('screener-result-table-results-sort')}
          aria-label="Daily Year to Date button, click this button to sort result table by Year to Date data."
          onClick={() => sortByHeader('yearToDate')}
          // TIAA Analytics Tracking
          data-analytics-linkname="By_yeartodate:InvestmentResults:Header"
          data-analytics-section="component"
          data-analytics-componenttype="Sort"
          // END TIAA Analytics Tracking
        >
          <TableColumnHeader
            sortingState={sortingState}
            sortString={'yearToDate'}
            sortLabel={'Daily'}
            lineTwo={'YTD'}
          />
          {sortingState.sortBy === 'yearToDate' ? (
            <div {...className(getSortingDirection())} />
          ) : (
            <div {...className('arrow-unselected')} />
          )}
        </button>
      </div>
      <div {...className('column-1y')}>
        <HeaderColumnSortLabel
          label="One Year button, click this button to sort result table by one Year performance data."
          clickCallback={() => sortByHeader('oneYear')}
          linkname="By_year:InvestmentResults:Header"
          section="component"
          componenttype="Sort"
          sortingState={sortingState}
          sortString="oneYear"
          sortLabel="1 Y"
          sortDirection={getSortingDirection()}
        ></HeaderColumnSortLabel>
      </div>
      <div {...className('column-3y')}>
        <HeaderColumnSortLabel
          label="Three Year button, click this button to sort result table by three Year performance data."
          clickCallback={() => sortByHeader('threeYear')}
          linkname="By_year:InvestmentResults:Header"
          section="component"
          componenttype="Sort"
          sortingState={sortingState}
          sortString="threeYear"
          sortLabel="3 Y"
          sortDirection={getSortingDirection()}
        ></HeaderColumnSortLabel>
      </div>
      <div {...className('column-5y')}>
        <HeaderColumnSortLabel
          label="Five Year button, click this button to sort result table by five Year performance data."
          clickCallback={() => sortByHeader('fiveYear')}
          linkname="By_year:InvestmentResults:Header"
          section="component"
          componenttype="Sort"
          sortingState={sortingState}
          sortString="fiveYear"
          sortLabel="5 Y"
          sortDirection={getSortingDirection()}
        ></HeaderColumnSortLabel>
      </div>
      <div {...className('column-10y')}>
        <HeaderColumnSortLabel
          label="Ten Year button, click this button to sort result table by ten Year performance data."
          clickCallback={() => sortByHeader('tenYear')}
          linkname="By_year:InvestmentResults:Header"
          section="component"
          componenttype="Sort"
          sortingState={sortingState}
          sortString="tenYear"
          sortLabel="10 Y"
          sortDirection={getSortingDirection()}
        ></HeaderColumnSortLabel>
      </div>
      <div {...className('column-inception')}>
        <HeaderColumnSortLabel
          label="Since Inception date button, click this button to sort result table by Since Inception date."
          clickCallback={() => sortByHeader('sinceIncep')}
          linkname="Incep_date:InvestmentResults:Header"
          section="component"
          componenttype="Sort"
          sortingState={sortingState}
          sortString="sinceIncep"
          sortLabel="Since Incep"
          sortDirection={getSortingDirection()}
        ></HeaderColumnSortLabel>
        <div aria-label="Inception date">
          <div {...className('text-nowrap')}>
            Incep Date
            <ButtonViewGlossary
              className="no-print"
              glossarySection="Inception Date"
              isScreener
              solid
            />
          </div>
        </div>
      </div>
      <div {...className('column-expenses')}>
        <button
          {...className('flex-nowrap', 'screener-result-table-results-sort')}
          aria-label="Net Expense button, click this button to sort result table by Net Expense."
          onClick={() => sortByHeader('netExp')}
          // TIAA Analytics Tracking
          data-analytics-linkname="Net_gross_exp:InvestmentResults:Header"
          data-analytics-section="component"
          data-analytics-componenttype="Sort"
          // END TIAA Analytics Tracking
        >
          <TableColumnHeader
            sortingState={sortingState}
            sortString={'netExp'}
            sortLabel={'Net Exp (%)'}
          />
          {sortingState.sortBy === 'netExp' ? (
            <div {...className(getSortingDirection())} />
          ) : (
            <div {...className('arrow-unselected')} />
          )}
        </button>
        <div aria-label="Gross Expense" {...className('text-nowrap')}>
          Gross Exp (%)
          <ButtonViewGlossary
            className="no-print"
            glossarySection="Gross / Net Expense Ratio"
            isScreener
            solid
          />
        </div>
      </div>
      <div {...className('column-rating', 'd-print-none')}>
        <div {...className('d-flex')}>
          <button
            {...className('rating-sort')}
            aria-label="Rating button, click this button to sort result table by fund rating."
            onClick={() => sortByHeader('rating')}
            // TIAA Analytics Tracking
            data-analytics-linkname="Morningstar:InvestmentResults:Header"
            data-analytics-section="component"
            data-analytics-componenttype="Sort"
            // END TIAA Analytics Tracking
          >
            <TableColumnHeader
              sortingState={sortingState}
              sortString={'rating'}
              sortLabel={'Morningstar'}
              lineTwo={'Overall Rating'}
              sup={numberedDisclaimers[FOOTNOTE_DISCLAIMERS.MSTARNUMBERED]?.number}
              lineThree={'Category'}
            />
            {sortingState.sortBy === 'rating' ? (
              <div {...className(getSortingDirection())} />
            ) : (
              <div {...className('arrow-unselected')} />
            )}
          </button>
          <ButtonViewGlossary
            className="no-print"
            glossarySection="Overall Morningstar Rating"
            isScreener
            solid
          />
        </div>

        <div {...className('mstar-date')}>As of {mStarAsOf ? format.oaDate(mStarAsOf) : '--'}</div>
      </div>

      <div {...className('column-glossary-bottom-spacer')}></div>
    </div>
  );
};

ResultTableHeader.propTypes = {
  hasClassA: PropTypes.bool,
  onChangePerformanceTerm: PropTypes.func,
  performanceQuarterly: PropTypes.bool,
  onClickExpandAll: PropTypes.func,
  expandAll: PropTypes.bool,
  mStarAsOf: PropTypes.number,
  performanceAsOf: PropTypes.number,
  setShowSalesCharge: PropTypes.func,
  showSalesCharge: PropTypes.bool,
  sortingState: PropTypes.object,
  sortByHeader: PropTypes.func,
  numberedDisclaimers: PropTypes.object
};

const HeaderColumnSortLabel = ({
  label,
  clickCallback,
  linkname,
  section,
  componenttype,
  sortingState,
  sortString,
  sortLabel,
  sortDirection
}) => (
  <button
    {...className('screener-result-table-results-sort')}
    aria-label={label}
    onClick={clickCallback}
    // TIAA Analytics Tracking
    data-analytics-linkname={linkname}
    data-analytics-section={section}
    data-analytics-componenttype={componenttype}
    // END TIAA Analytics Tracking
  >
    <TableColumnHeader sortingState={sortingState} sortString={sortString} sortLabel={sortLabel} />
    {sortingState.sortBy === sortString ? (
      <div {...className(sortDirection)} />
    ) : (
      <div {...className('arrow-unselected')} />
    )}
  </button>
);

HeaderColumnSortLabel.propTypes = {
  label: PropTypes.string,
  clickCallback: PropTypes.func,
  linkname: PropTypes.string,
  section: PropTypes.string,
  componenttype: PropTypes.string,
  sortingState: PropTypes.object,
  sortString: PropTypes.string,
  sortLabel: PropTypes.string,
  sortBy: PropTypes.string,
  sortDirection: PropTypes.string
};

const mapStateToProps = state => {
  return {
    numberedDisclaimers: state.resultData.footnoteDisclaimers.numberedDisclaimers
  };
};

export default connect(mapStateToProps, null)(ResultTableHeader);
